import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TableKit } from '@tiptap/extension-table';
import { Icon } from '../../components/Icon.tsx';
import { dv } from '../../i18n/dv.ts';
import { useAuthStore } from '../../store/auth.ts';
import { noteBodyToHtml } from '../../lib/noteHtml.ts';
import { notesApi, noteQueryKey, pageIdOf, pageLabel, type NotePage, type PageId } from './api.ts';
import { useScrollMemory, PREVIEW_SCROLL_PREFIX } from './useScrollMemory.ts';

interface ScriptViewProps {
  /** The CURRENTLY OPEN page's live document, unsaved edits included. */
  html: string;
  /** The reader's own notebook, so they can page through it without leaving. */
  pages: NotePage[];
  /** Which page `html` belongs to. */
  activePageId: PageId;
  meetingId: string;
  onClose: () => void;
}

// Lectern zoom. The chair reads this aloud, and how far the screen is from
// their eyes is a property of the room, not of the document — so the setting is
// remembered per device rather than reset on every open.
const ZOOM_KEY = 'wapmeet.lecternZoom';
const ZOOM_MIN = 0.6;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.1;

const clampRange = (z: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
/** Stepped, for the buttons and the keyboard — lands on round 10% values. */
const clampZoom = (z: number): number => clampRange(Math.round(z * 10) / 10);
/** Continuous, for pinch — 1% granularity so the gesture tracks the fingers. */
const clampZoomFine = (z: number): number => clampRange(Math.round(z * 100) / 100);

function readStoredZoom(): number {
  // Storage can be absent or throw outright (private windows, blocked site
  // data). A lectern that fails to open because of a zoom preference would be
  // a far worse bug than one that opens at 100%.
  try {
    const raw = window.localStorage.getItem(ZOOM_KEY);
    if (raw === null) return 1;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? clampZoom(parsed) : 1;
  } catch {
    return 1;
  }
}

/**
 * Full-screen READ-ONLY view of the notepad — the lectern mode. The chair reads
 * their script off this while actually chairing, so it is large, uncluttered
 * and, deliberately, not editable: a stray keypress at the lectern must not be
 * able to alter the document, and nothing here can fire a save.
 *
 * Rendered through a non-editable TipTap instance rather than
 * dangerouslySetInnerHTML: the same parser and the same `.note-prose` styles as
 * the editor, with no raw-HTML injection point in the app at all.
 *
 * Shows what is on screen NOW, including edits that have not yet been saved.
 */
export function ScriptView({ html, pages, activePageId, meetingId, onClose }: ScriptViewProps): JSX.Element {
  const initial = useRef(html);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Which page is being READ. Starts on the page the preview was opened from
  // and moves with the tabs — deliberately without touching the route, because
  // the page component unmounts this overlay while a different note loads, and
  // the chair would be thrown out of full screen mid-meeting.
  const [viewing, setViewing] = useState<PageId>(activePageId);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const onActive = viewing === activePageId;

  // Other pages are fetched read-only. Same query key as the editor uses, so
  // this reads the cache rather than refetching a note the app already has.
  const other = useQuery({
    queryKey: noteQueryKey(userId, meetingId, viewing),
    queryFn: () => notesApi.fetchNote(meetingId, viewing),
    enabled: !onActive && userId !== null,
  });

  // The open page shows its LIVE document (what is on screen now, unsaved edits
  // included); any other page shows what the server has. noteBodyToHtml covers
  // notes written before the notepad became a rich-text editor.
  const shown = onActive ? html : (other.data ? noteBodyToHtml(other.data.body) : null);

  const editor = useEditor({
    content: initial.current,
    editable: false,
    extensions: [
      // h4 MUST be in the schema even though the toolbar only offers h2/h3:
      // the generated chair script marks every agenda item as an h4, and a
      // level TipTap does not know is parsed as a plain paragraph — which
      // autosave would then persist, destroying those headings for good.
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      TextAlign.configure({ types: ['heading', 'paragraph'], defaultAlignment: 'right' }),
      // Same schema as the editor, minus resizing: this surface is read-only,
      // so a table here must render but must never be draggable. A node the
      // editor can produce that this view cannot parse would show the chair a
      // mangled script at the lectern.
      TableKit.configure({ table: { resizable: false, renderWrapper: true } }),
    ],
    editorProps: { attributes: { dir: 'rtl', class: 'note-prose' } },
  });

  useEffect(() => () => { editor?.destroy(); }, [editor]);

  // Swap the document when the reader changes tab. `emitUpdate: false` is not
  // strictly needed on a non-editable instance — nothing here is wired to a
  // save — but it keeps that true by construction rather than by luck.
  // Each page remembers where it was being read, under its own namespace: the
  // preview lays text out far larger than the editor, so the same offset is not
  // the same place in the two surfaces.
  const scrollEl = useRef<HTMLDivElement | null>(null);
  const { resettle } = useScrollMemory(
    scrollEl,
    editor ? `${meetingId}:${viewing}` : undefined,
    PREVIEW_SCROLL_PREFIX,
  );

  useEffect(() => {
    if (!editor) return;
    // `null` means the chosen page is still loading (or failed). Blank it
    // rather than leaving the previous page's text sitting under a tab that
    // now says something else.
    editor.commands.setContent(shown ?? '', { emitUpdate: false });
    // The document has just been REPLACED, so the restore has to start again:
    // an offset applied against the outgoing (or an empty) document is clamped
    // away, which is exactly what happens when a tab's note arrives from the
    // network a moment after the tab is clicked.
    resettle();
  }, [editor, shown, resettle]);

  const [zoom, setZoom] = useState(readStoredZoom);
  // The key handler is bound once; a ref lets it read the CURRENT zoom without
  // re-binding on every step (and without a stale closure holding the old one).
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const persistZoom = useCallback((z: number): void => {
    try { window.localStorage.setItem(ZOOM_KEY, String(z)); } catch { /* not worth surfacing */ }
  }, []);

  const applyZoom = useCallback((next: number): void => {
    const z = clampZoom(next);
    setZoom(z);
    persistZoom(z);
  }, [persistZoom]);

  // --- Pinch to zoom -------------------------------------------------------
  // Two fingers resize the DOCUMENT rather than the viewport. The browser's own
  // pinch would magnify the whole overlay — header included — and leave the
  // chair panning sideways to read a line. Scaling the text instead keeps it
  // reflowed to the width of the screen, which is the whole point at a lectern.
  //
  // Vertical panning is still the browser's job (`touch-action: pan-y` below),
  // so reading a long script is untouched; only the pinch gesture is ours.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; zoom: number } | null>(null);

  const spread = (): number => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  function onPointerDown(e: React.PointerEvent): void {
    if (e.pointerType !== 'touch') return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) pinch.current = { dist: spread(), zoom: zoomRef.current };
  }

  function onPointerMove(e: React.PointerEvent): void {
    if (e.pointerType !== 'touch') return;
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const start = pinch.current;
    if (!start || pointers.current.size !== 2 || start.dist === 0) return;
    setZoom(clampZoomFine(start.zoom * (spread() / start.dist)));
  }

  function endPointer(e: React.PointerEvent): void {
    if (e.pointerType !== 'touch') return;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size >= 2) return;
    // Write once when the fingers lift, not on every frame of the gesture.
    if (pinch.current) { persistZoom(zoomRef.current); pinch.current = null; }
  }

  // Esc closes, matching every other full-screen surface people expect.
  // +/- zoom and 0 resets, the shortcuts every reader already knows — safe to
  // bind unconditionally because this surface is read-only and has no text
  // input to steal them from.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') { closeRef.current(); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return; // leave browser zoom alone
      if (e.key === '+' || e.key === '=') { e.preventDefault(); applyZoom(zoomRef.current + ZOOM_STEP); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); applyZoom(zoomRef.current - ZOOM_STEP); }
      else if (e.key === '0') { e.preventDefault(); applyZoom(1); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [applyZoom]);

  // Ask for true browser full-screen so the chair loses the browser chrome too.
  // Entirely best-effort: it needs a user gesture, can be refused by policy, and
  // is unavailable on iOS Safari — the overlay below is the real mechanism and
  // works regardless.
  useEffect(() => {
    const el = document.documentElement;
    void el.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => undefined);
    return () => {
      if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => undefined);
    };
  }, []);

  // Don't let the page behind scroll while the overlay is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      ref={scrollEl}
      role="dialog"
      aria-modal="true"
      aria-label={dv.notes.title}
      className="fixed inset-0 z-50 overflow-y-auto bg-[hsl(var(--paper))]"
      // pan-y keeps vertical reading-scroll native while handing the pinch
      // gesture to us instead of to the browser's viewport zoom.
      style={{ touchAction: 'pan-y' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <div className="sticky top-0 z-10 bg-[hsl(var(--paper))]/95 backdrop-blur">
      <div className="flex items-center justify-end gap-2 px-4 py-3">
        {/* Zoom is labelled with symbols and a percentage rather than words:
            WapMeet has no Dhivehi copy for it anywhere in the ecosystem, and
            «−», «+» and «%» read the same in every language. */}
        <div className="flex items-center gap-1 rounded-full bg-[hsl(var(--secondary-container))] px-1 text-[hsl(var(--on-secondary-container))]">
          <button
            type="button"
            onClick={() => applyZoom(zoom - ZOOM_STEP)}
            disabled={zoom <= ZOOM_MIN}
            className="grid size-9 place-items-center rounded-full transition-colors hover:brightness-95 disabled:opacity-40"
            aria-label="−"
          >
            <Icon name="remove" size={20} />
          </button>
          <button
            type="button"
            onClick={() => applyZoom(1)}
            className="min-w-14 rounded-full px-1 text-center text-sm font-semibold tabular-nums transition-colors hover:brightness-95"
            aria-live="polite"
            aria-label={`${Math.round(zoom * 100)}%`}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            onClick={() => applyZoom(zoom + ZOOM_STEP)}
            disabled={zoom >= ZOOM_MAX}
            className="grid size-9 place-items-center rounded-full transition-colors hover:brightness-95 disabled:opacity-40"
            aria-label="+"
          >
            <Icon name="add" size={20} />
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid size-11 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] transition-colors hover:brightness-95"
          aria-label={dv.common.close}
          title={dv.common.close}
        >
          <Icon name="close" size={22} />
        </button>
      </div>

      {/* Page tabs — switch note without leaving full screen. Only worth the
          space once there is somewhere else to go. */}
      {pages.length > 1 && (
        <div
          role="tablist"
          aria-label={dv.notes.title}
          className="flex gap-1 overflow-x-auto px-4 pb-2"
          // The overlay is pan-y so the pinch gesture is ours; this strip needs
          // its own pan-x or it cannot be scrolled sideways by touch.
          style={{ touchAction: 'pan-x' }}
        >
          {pages.map((page) => {
            const id = pageIdOf(page);
            const isOpen = id === viewing;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={isOpen}
                onClick={() => setViewing(id)}
                // The selected tab is the only FILLED one; the rest are
                // outlined. A row of filled pills reads as a row of equal
                // things, which is precisely what a tab strip is not. The
                // border is on both so the two states are the same size and
                // the strip does not shift as you move between them.
                className={[
                  'shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors',
                  isOpen
                    ? 'border-transparent bg-[hsl(var(--secondary-container))] font-semibold text-[hsl(var(--on-secondary-container))]'
                    : page.hasNote
                      ? 'border-border text-muted-foreground hover:bg-[hsl(var(--chrome))]'
                      // Nothing written here yet — still reachable, just quieter.
                      : 'border-border/60 text-muted-foreground/60 hover:bg-[hsl(var(--chrome))]',
                ].join(' ')}
              >
                {pageLabel(page)}
              </button>
            );
          })}
        </div>
      )}
      </div>

      {!onActive && other.isPending && (
        <p className="px-6 py-4 text-sm text-muted-foreground">{dv.common.loading}</p>
      )}
      {!onActive && other.isError && (
        <p className="px-6 py-4 text-sm text-muted-foreground" role="alert">{dv.common.error}</p>
      )}
      <div
        className="note-editor note-present mx-auto max-w-4xl px-6 pb-24"
        style={{ '--lectern-zoom': zoom } as React.CSSProperties}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
